import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { Menu, X, Briefcase, User, LogOut, ChevronDown } from "lucide-react";

const links = [
  { to: "/", label: "Home" },
  { to: "/jobs", label: "Jobs" },
  { to: "/services", label: "Services" },
  { to: "/for-employers", label: "For Employers" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

export default function Header() {
  const { user, logout } = useAuth();
  const loc = useLocation();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [userOpen, setUserOpen] = useState(false);

  const dashPath = user?.role === "admin" || user?.role === "recruiter" ? "/admin"
    : user?.role === "employer" ? "/employer" : "/candidate";

  return (
    <header className="sticky top-0 z-50 bg-[#0F2A4A] text-white shadow-lg" data-testid="site-header">
      <div className="ae-container flex items-center justify-between h-20">
        <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
          <div className="w-10 h-10 rounded-lg bg-[#C5A059] flex items-center justify-center">
            <Briefcase className="w-5 h-5 text-white" />
          </div>
          <div className="leading-tight">
            <div className="font-heading font-semibold text-lg tracking-tight">Achievers</div>
            <div className="text-[10px] tracking-[0.25em] uppercase text-[#C5A059]">Enterprise</div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-8">
          {links.map(l => (
            <Link key={l.to} to={l.to}
              data-testid={`nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
              className={`text-sm font-medium transition-colors ${
                loc.pathname === l.to ? "text-[#C5A059]" : "text-white/85 hover:text-white"
              }`}>
              {l.label}
            </Link>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          {user ? (
            <div className="relative">
              <button onClick={() => setUserOpen(!userOpen)} data-testid="user-menu-btn"
                className="flex items-center gap-2 px-4 py-2 rounded-xl border border-white/20 hover:bg-white/10 transition-all text-sm">
                <User className="w-4 h-4" />
                <span>{user.full_name.split(" ")[0]}</span>
                <ChevronDown className="w-4 h-4" />
              </button>
              {userOpen && (
                <div className="absolute right-0 top-full mt-2 w-52 bg-white text-[#0F2A4A] rounded-xl shadow-xl border border-slate-100 overflow-hidden">
                  <Link to={dashPath} onClick={() => setUserOpen(false)}
                    data-testid="menu-dashboard"
                    className="block px-4 py-3 hover:bg-slate-50 text-sm">Dashboard</Link>
                  <button onClick={() => { logout(); setUserOpen(false); nav("/"); }}
                    data-testid="menu-logout"
                    className="w-full text-left px-4 py-3 hover:bg-slate-50 text-sm flex items-center gap-2 border-t border-slate-100">
                    <LogOut className="w-4 h-4" /> Sign Out
                  </button>
                </div>
              )}
            </div>
          ) : (
            <>
              <Link to="/login" data-testid="header-login"
                className="text-sm font-medium text-white/85 hover:text-white">Sign In</Link>
              <Link to="/register" data-testid="header-register"
                className="ae-btn-primary text-sm">Get Started</Link>
            </>
          )}
        </div>

        <button className="lg:hidden text-white" onClick={() => setOpen(!open)} data-testid="mobile-menu-btn">
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden bg-[#0A1F38] border-t border-white/10">
          <div className="ae-container py-4 flex flex-col gap-2">
            {links.map(l => (
              <Link key={l.to} to={l.to} onClick={() => setOpen(false)}
                className="py-2 text-white/85 hover:text-[#C5A059]">
                {l.label}
              </Link>
            ))}
            <div className="pt-3 border-t border-white/10 flex flex-col gap-2">
              {user ? (
                <>
                  <Link to={dashPath} onClick={() => setOpen(false)} className="ae-btn-primary text-center text-sm">Dashboard</Link>
                  <button onClick={() => { logout(); setOpen(false); nav("/"); }} className="ae-btn-outline text-white border-white text-sm">Sign Out</button>
                </>
              ) : (
                <>
                  <Link to="/login" onClick={() => setOpen(false)} className="text-center py-2 text-white/85">Sign In</Link>
                  <Link to="/register" onClick={() => setOpen(false)} className="ae-btn-primary text-center text-sm">Get Started</Link>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
